/**
 * @description Deployment Detector Service
 * @description Detects when backend is deploying based on CORS errors and failures,
 * @note Please Do not remove any comment from this file this is importent to understand the whole Deployment fallback flow
 */

class DeploymentDetector {
	constructor() {
		this.failureCount = 0;
		this.failureThreshold = 3; // Number of consecutive failures to trigger deployment mode
		this.isDeploymentMode = false;
		this.listeners = [];
		this.resetTimer = null;
		this.lastErrorTime = null;
		this.errorTimeWindow = 5000; // 5 seconds window for errors to be considered related
		this.lastNotifyTime = 0;
		this.notifyThrottle = 100; // Minimum 100ms between notifications to prevent render storms

		// Listen for browser online/offline events
		// If the user goes offline, exit deployment mode (it's a network issue, not deployment)
		if (typeof window !== 'undefined') {
			window.addEventListener('offline', () => {
				if (this.isDeploymentMode) {
					this.exitDeploymentMode();
				}
				this.resetFailureCount();
			});
		}

		// Restore deployment mode state from sessionStorage on initialization
		this.restoreDeploymentState();
	}

	/**
	 * Restore deployment mode state from sessionStorage (To prevent 502 status code show when refresh page during deployment)
	 */
	restoreDeploymentState() {
		try {
			const savedState = sessionStorage.getItem('deploymentMode');
			if (savedState) {
				const { isDeploying, timestamp } = JSON.parse(savedState);
				const now = Date.now();

				// If deployment mode was active within the last 5 minutes, restore it
				// But only if the browser is online — if offline, it's a network issue
				if (isDeploying && now - timestamp < 300000 && navigator.onLine) {
					this.isDeploymentMode = true;
					// eslint-disable-next-line no-console
					console.log('Restored deployment mode from previous session');
					// Notify listeners after a short delay to ensure components are mounted
					setTimeout(() => this.notifyListeners(true), 100);
				} else {
					// Clear stale state
					sessionStorage.removeItem('deploymentMode');
				}
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error restoring deployment state:', error);
		}
	}

	/**
	 * Save deployment mode state to sessionStorage
	 */
	saveDeploymentState(isDeploying) {
		try {
			if (isDeploying) {
				sessionStorage.setItem(
					'deploymentMode',
					JSON.stringify({
						isDeploying: true,
						timestamp: Date.now()
					})
				);
			} else {
				// Remove from sessionStorage when deployment is done
				sessionStorage.removeItem('deploymentMode');
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error saving deployment state:', error);
		}
	}

	/**
	 * Check if error indicates a deployment scenario
	 */
	isDeploymentError(error) {
		// Check for CORS errors
		if (error?.message?.toLowerCase().includes('cors')) {
			return true;
		}

		// Check for network errors (ERR_NETWORK, ERR_CONNECTION_REFUSED, etc.)
		if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
			return true;
		}

		// Check for 502/503 status codes (deployment status code)
		if (error?.response?.status === 502 || error?.response?.status === 503) {
			return true;
		}

		// Check if there's no response at all (server unreachable)
		if (error?.request && !error?.response) {
			return true;
		}

		return false;
	}

	/**
	 * Record an API error (throttled to prevent performance issues)
	 */
	recordError(error) {
		// If the browser is offline, this is a network connectivity issue, not a deployment
		// Do NOT enter deployment mode for no-network scenarios
		if (typeof navigator !== 'undefined' && !navigator.onLine) {
			return false;
		}

		// If already in deployment mode, just return early to avoid unnecessary processing
		// This prevents thousands of calls during deployment
		if (this.isDeploymentMode) {
			return true;
		}

		const now = Date.now();

		// If this is a deployment-related error
		if (this.isDeploymentError(error)) {
			// Check if error is within time window
			if (this.lastErrorTime && now - this.lastErrorTime > this.errorTimeWindow) {
				// Too much time has passed, reset counter
				this.failureCount = 1;
			} else {
				this.failureCount++;
			}

			this.lastErrorTime = now;

			// If hit the threshold, enter deployment mode
			if (this.failureCount >= this.failureThreshold) {
				this.enterDeploymentMode();
			}

			// Clear any existing reset timer
			if (this.resetTimer) {
				clearTimeout(this.resetTimer);
			}

			// Set a new reset timer
			this.resetTimer = setTimeout(() => {
				this.resetFailureCount();
			}, this.errorTimeWindow * 2);

			return true; // Indicate this was a deployment error
		}

		return false; // Not a deployment error
	}

	/**
	 * Record a successful API call
	 */
	recordSuccess() {
		// If we're in deployment mode and getting successful calls, exit it
		if (this.isDeploymentMode) {
			this.exitDeploymentMode();
		}
		this.resetFailureCount();
	}

	/**
	 * Reset failure count
	 */
	resetFailureCount() {
		this.failureCount = 0;
		this.lastErrorTime = null;
		if (this.resetTimer) {
			clearTimeout(this.resetTimer);
			this.resetTimer = null;
		}
	}

	/**
	 * Enter deployment mode
	 */
	enterDeploymentMode() {
		if (this.isDeploymentMode) return;

		// Do not enter deployment mode if the browser is offline (no network)
		if (typeof navigator !== 'undefined' && !navigator.onLine) {
			return;
		}

		this.isDeploymentMode = true;
		// eslint-disable-next-line no-console
		console.log('Deployment mode activated - Backend appears to be deploying');
		this.saveDeploymentState(true);
		this.notifyListeners(true);

		// No automatic recovery check - users can manually reload the page
	}

	/**
	 * Exit deployment mode
	 */
	exitDeploymentMode() {
		if (!this.isDeploymentMode) return;

		this.isDeploymentMode = false;
		// eslint-disable-next-line no-console
		console.log('Deployment mode deactivated - Backend is back online');
		this.saveDeploymentState(false);
		this.notifyListeners(false);
		this.resetFailureCount();
	}

	/**
	 * Subscribe to deployment mode changes
	 */
	subscribe(listener) {
		this.listeners.push(listener);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== listener);
		};
	}

	/**
	 * Notify all listeners (throttled to prevent render storms)
	 */
	notifyListeners(isDeploying) {
		const now = Date.now();

		// Throttle notifications to prevent excessive re-renders during error storms
		if (now - this.lastNotifyTime < this.notifyThrottle) {
			return;
		}

		this.lastNotifyTime = now;

		this.listeners.forEach((listener) => {
			try {
				listener(isDeploying);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error in deployment listener:', error);
			}
		});
	}

	/**
	 * Check if currently in deployment mode
	 */
	isDeploying() {
		return this.isDeploymentMode;
	}

	/**
	 * Manually trigger deployment mode (for testing)
	 */
	triggerDeploymentMode() {
		this.enterDeploymentMode();
	}

	/**
	 * Manually exit deployment mode (for testing)
	 */
	clearDeploymentMode() {
		this.exitDeploymentMode();
	}
}

// Create and export singleton instance (singleton pattern)
const deploymentDetector = new DeploymentDetector();

export default deploymentDetector;
